/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/KanbanBoardSettingsPanel" />
import * as React from "react";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { useTfsProject } from "Tfs/Platform/TfsComponent";
import { ObservableArray } from "VSS/Core/Observable";
import { Components, usePageContext } from "VSS/Platform/Layout";
import { IListBoxGroup } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { divyCardRules } from "Wit/BoardsHub/Common/StylesUtils";
import {
    getAnnotationData,
    getAnnotationSettingsView
} from "Wit/Common/KanbanBoardSettings/Components/AnnotationSettingsView/AnnotationSettingsView";
import { makeColumnsSettings } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/ColumnSettings";
import { getColumnsSettingsTab } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/ColumnSettingsView";
import { makeSwimlanesSettings } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/SwimlanesSettings";
import { getSwimlanesSettingsTab } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/SwimlanesSettingsView";
import { getBoardStatusBadgeTab } from "Wit/Common/KanbanBoardSettings/Components/BoardStatusBadgeView";
import { getCardReorderingTab } from "Wit/Common/KanbanBoardSettings/Components/CardReorderingView";
import { getTestSettingsData, getTestSettingsTab } from "Wit/Common/KanbanBoardSettings/Components/TestSettingsView/TestSettingsView";
import {
    saveAnnotationSettings,
    saveBoardColumnSettings,
    saveBoardFieldSettings,
    saveBoardRowSettings,
    saveCardReorderSettings,
    saveCardStyleSettings,
    saveStatusBadgeSettings,
    saveTagStyleSettings,
    saveTestSettings
} from "Wit/Common/KanbanBoardSettings/Utils/SaveSettingsUtils";
import { IKanbanBoardSettingsPanelProps } from "Wit/Common/KanbanBoardSettingsTypes/Components/KanbanBoardSettingsPanel.Props";
import { TeamSettingsPanel } from "Wit/Common/TeamSettingsPanel/Components/TeamSettingsPanel";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { BoardFieldSettings, CardSettingsDict } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettings";
import { getBoardFieldSettingsTab } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettingsView";
import { getStyleSettingDataFromCardRules, IStyleService } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleService";
import { getStyleSettingsTab } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleTable";
import { getTagStylesFromCardRules, getTagStylesSettingsView } from "Wit/Common/WitSettingsViews/Components/TagStylesTable/TagStylesTable";

export const KanbanBoardSettingsPanel: React.FunctionComponent<IKanbanBoardSettingsPanelProps> = ({
    boardSettingsData,
    onDismiss,
    onSave,
    onBeforeSettingsSaved,
    teamId
}) => {
    const pageContext = usePageContext();
    const projectId = useTfsProject()?.id ?? "";
    return (
        <Observer boardSettingsData={boardSettingsData}>
            {() => {
                if (!boardSettingsData.value) {
                    return null;
                }

                const { annotationSettings, boardData, effortField } = boardSettingsData.value;
                const effortFieldReference = effortField ? { id: effortField.id, referenceName: effortField.referenceName } : undefined;
                const boardId = boardData.boardModel.board.id;
                const disabled = !boardData.boardModel.boardSettings.canEdit;
                const teamContext = {
                    teamId: boardData.backlogContextData.team.id,
                    projectId
                } as TeamContext;

                // card settings
                const { cardStyles, tagStyles } = divyCardRules(boardData.boardModel.boardCardSettings.styles);
                const rules = getStyleSettingDataFromCardRules(cardStyles);
                const boardFieldSettings = new BoardFieldSettings(boardData.boardModel.boardCardSettings.cards as unknown as CardSettingsDict);
                const tagStylesSettings = new ObservableArray(getTagStylesFromCardRules(tagStyles));
                const annotationData = getAnnotationData(annotationSettings);
                const originalTestSettings = boardData.boardModel.boardCardSettings.testSettings;
                const testSettings = getTestSettingsData(originalTestSettings);

                // board settings
                const columnSettings = makeColumnsSettings(boardData);
                const swimlanesSettings = makeSwimlanesSettings(boardData);

                const settingsViews: ISettingsView[] = [
                    // cards settings
                    getBoardFieldSettingsTab(boardFieldSettings, effortFieldReference, disabled, KanbanBoardSettingsGroup.Cards, () =>
                        saveBoardFieldSettings(pageContext, teamContext, boardId, boardFieldSettings)
                    ),
                    getStyleSettingsTab(pageContext, rules, disabled, KanbanBoardSettingsGroup.Cards, () =>
                        saveCardStyleSettings(pageContext, teamContext, boardId)
                    ),
                    getTagStylesSettingsView(tagStylesSettings, disabled, KanbanBoardSettingsGroup.Cards, () =>
                        saveTagStyleSettings(pageContext, teamContext, boardId, tagStylesSettings.value)
                    ),
                    getAnnotationSettingsView(annotationData, annotationSettings, disabled, KanbanBoardSettingsGroup.Cards, () =>
                        saveAnnotationSettings(pageContext, teamContext, boardId, annotationData)
                    ),
                    getTestSettingsTab(testSettings, originalTestSettings, disabled, KanbanBoardSettingsGroup.Cards, () =>
                        saveTestSettings(pageContext, teamId, testSettings)
                    ),

                    // Board settings
                    getColumnsSettingsTab(columnSettings, disabled, KanbanBoardSettingsGroup.Boards, () =>
                        saveBoardColumnSettings(pageContext, teamContext, boardId, columnSettings)
                    ),
                    getSwimlanesSettingsTab(swimlanesSettings, disabled, KanbanBoardSettingsGroup.Boards, () =>
                        saveBoardRowSettings(pageContext, teamContext, boardId, swimlanesSettings)
                    ),
                    getCardReorderingTab(
                        boardData.boardModel.boardSettings.preserveBacklogOrder,
                        disabled,
                        KanbanBoardSettingsGroup.Boards,
                        preserveBacklogOrder => saveCardReorderSettings(pageContext, teamContext, boardId, preserveBacklogOrder)
                    ),
                    getBoardStatusBadgeTab(
                        boardData.boardModel.boardSettings.statusBadgeIsPublic,
                        teamId,
                        boardData.backlogContextData.levelName,
                        boardData.boardModel.board.id,
                        disabled,
                        KanbanBoardSettingsGroup.Boards,
                        isBadgePublic => saveStatusBadgeSettings(pageContext, teamContext.projectId, teamContext.teamId, boardId, isBadgePublic)
                    )
                ];

                const wrappedOnDismiss = () => {
                    const styleService = pageContext.getService<IStyleService>("IStyleService");
                    styleService.clear();
                    onDismiss();
                };

                return (
                    <TeamSettingsPanel
                        onDismiss={wrappedOnDismiss}
                        onBeforeSettingsSaved={onBeforeSettingsSaved}
                        onSave={onSave}
                        settingsGroups={settingsGroups}
                        settingsViews={settingsViews}
                        teamId={boardData.backlogContextData.team.id}
                        titleProps={{ text: "Settings" }}
                    />
                );
            }}
        </Observer>
    );
};

const KanbanBoardSettingsGroup = {
    Cards: "cards",
    Boards: "boards"
} as const;

const settingsGroups: IListBoxGroup[] = [
    {
        id: KanbanBoardSettingsGroup.Cards,
        name: "Cards"
    },
    {
        id: KanbanBoardSettingsGroup.Boards,
        name: "Boards"
    }
];

Components.add("ms.vss-work-web.kanban-board-settings-panel-component", KanbanBoardSettingsPanel);
